import React from "react";
import { CAlert } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { Link } from "react-router-dom";
import { formatCurrency } from "../../../utils/currency";
import { formatUTCDate } from "../../../utils/string";
import { useAppSelector } from "../../../store/hooks";
import { useAutopayNextCycle } from "./useAutopayNextCycle";
import { useResolvedAutopayPaymentMethod } from "./useAutopayPaymentMethodDisplay";
import { PaymentMethodSourceLabel } from "../../PaymentMethods/components/PaymentMethodSourceLabel";

interface PropsInterface {
  paymentMethodId?: string;
  className?: string;
  enrolled?: boolean;
  showPaymentMethodDetail?: boolean;
}

export const AutopayInfoAlert: React.FC<PropsInterface> = ({
  paymentMethodId,
  className,
  enrolled = false,
  showPaymentMethodDetail = true,
}) => {
  const paymentMethod = useResolvedAutopayPaymentMethod(paymentMethodId);
  const { hasUpcomingDebit, debitAmount, runDate } = useAutopayNextCycle();
  const isAutopayOwner = useAppSelector(
    (state) =>
      state.accountSummary.data?.[state.accountSummary.activeTenantAccountIndex]
        ?.autopay?.owner
  );

  const paymentMethodLabel = paymentMethod ? (
    <PaymentMethodSourceLabel paymentMethod={paymentMethod} />
  ) : (
    <strong>Payment method unavailable</strong>
  );

  const enrolledBody = !isAutopayOwner ? (
    <>
      {hasUpcomingDebit ? (
        <>
          AutoPay will debit <strong>{formatCurrency(debitAmount)}</strong> on
          or around <strong>{formatUTCDate(runDate)}</strong> from a delegate
          user&apos;s payment method.
        </>
      ) : (
        <>AutoPay will debit from a delegate user&apos;s payment method.</>
      )}
    </>
  ) : hasUpcomingDebit ? (
    <>
      AutoPay will debit <strong>{formatCurrency(debitAmount)}</strong> on or
      around <strong>{formatUTCDate(runDate)}</strong> from your AutoPay payment
      method: {paymentMethodLabel}.
    </>
  ) : (
    <>AutoPay will debit from your AutoPay payment method: {paymentMethodLabel}.</>
  );

  return (
    <CAlert color="info" className={className}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: "12px",
          rowGap: "8px",
          alignItems: "center",
        }}
      >
        <CIcon
          name="cis-info-circle"
          size="lg"
          style={{ gridColumn: 1, gridRow: 1, flexShrink: 0 }}
        />
        <span style={{ gridColumn: 2, gridRow: 1, fontWeight: 600 }}>
          {enrolled ? "AutoPay is Enabled." : "AutoPay will use this payment method"}
        </span>
        {(enrolled || showPaymentMethodDetail) && (
          <div style={{ gridColumn: 2, gridRow: 2 }}>
            {enrolled ? (
              enrolledBody
            ) : (
              <>
                Your next AutoPay debit will come from:
                <br />
                {paymentMethodLabel}.
              </>
            )}
          </div>
        )}
        {enrolled && isAutopayOwner && (
          <div style={{ gridColumn: 2, gridRow: 3 }} className="text-muted">
            Change your AutoPay payment method on the{" "}
            <Link to="/payment-methods">Payment Methods</Link> or{" "}
            <Link to="/auto-pay">AutoPay</Link> page.
          </div>
        )}
        {enrolled && !isAutopayOwner && (
          <div style={{ gridColumn: 2, gridRow: 3 }} className="text-muted">
            Contact the user who enrolled AutoPay to change or disable it.
          </div>
        )}
      </div>
    </CAlert>
  );
};
